import { AxiosResponse } from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { connect, useDispatch, useSelector } from "react-redux";
import { useParams, withRouter } from "react-router";
import { IAcademyCoursePackage, ICourseStep } from "src/academy/types";
import { localize } from "src/l10n";
import { IActiveUserProfile } from "src/profile/reducer";
import api from "src/spintr/SpintrApi";
import {
    Breadcrumbs,
    ContentWithInfoPanel,
    Label,
    PageHeader,
    setAdminNoPadding,
    setDisableWrapperBackground,
} from "src/ui";
import SpintrLoader from "src/ui/components/Loader";
import { DefaultButton } from "@fluentui/react";
import { setSidebarMode, VisageSidebarMode } from "src/sidebar";
import AcademyImage from "../AcademyImage/AcademyImage";
import AcademyCourseListItem from "src/academy/courses/AcademyCourseListItem/AcademyCourseListItem";
import AcademyCourseMetaDataRow from "../AcademyCourseMetaDataRow/AcademyCourseMetaDataRow";
import AcademyCourseProgress from "../AcademyCourseProgress/AcademyCourseProgress";
import { Link } from "react-router-dom";
import AcademyCourseSidebarSection from "../sidebar/AcademyCourseSidebarSection/AcademyCourseSidebarSection";
import AcademyCourseSidebarMetaRow from "../sidebar/AcademyCourseSidebarMetaRow/AcademyCourseSidebarMetaRow";
import moment from "moment";
import languages from "src/utils/languages";
import { SpintrTypes } from "src/typings";
import "./AcademyCoursePackageView.scss";

interface IProps {
    currentUser?: IActiveUserProfile;
    appEnabled?: boolean;
    appMode?: boolean;
    academyAppName?: string;
    history?: any;
    isSmallViewMode?: boolean;
}

interface IState {
    isLoading: boolean;
    coursePackage?: IAcademyCoursePackage;
}

interface IRouteParams {
    id: string;
}

const AcadmeyCoursePackageView = (props: IProps) => {
    const dispatch = useDispatch();
    const params = useParams<IRouteParams>();
    const [state, setState] = useState<IState>({
        isLoading: true,
    });

    const isAdminOrEditor = useSelector<Spintr.AppState, boolean>(
        (state) => state.profile.active.isAdmin || state.profile.active.isEditor
    );
    const appMode = useSelector<Spintr.AppState, boolean>((state) => state.ui.appMode);

    useEffect(() => {
        if (!props.appEnabled) {
            return props.history.push("/404");
        }

        dispatch(setDisableWrapperBackground(true));
        dispatch(setAdminNoPadding(true));
        dispatch(setSidebarMode(VisageSidebarMode.noMenu));

        return () => {
            dispatch(setDisableWrapperBackground(false));
            dispatch(setAdminNoPadding(false));
            dispatch(setSidebarMode(VisageSidebarMode.menu));
        };
    }, []);

    useEffect(() => {
        setState((s) => ({
            ...s,
            isLoading: true,
        }));

        fetchCourse();
    }, [params.id]);

    const fetchCourse = useCallback(() => {
        const id = params.id;

        api.get("/api/v1/academy/coursepackages/" + id)
            .then((response: AxiosResponse) => {
                setState((s: IState) => ({
                    ...s,
                    isLoading: false,
                    coursePackage: response.data,
                }));
            })
            .catch(() => {});
    }, [params.id]);

    const renderSidebar = () => (
        <div className="AcademyCourseSidebar">
            <AcademyCourseSidebarSection
                title={localize("COURSE_PACKAGE_DETAILS")}
                onRender={() => (
                    <div className="AcademyCourseSidebarMeta">
                        {state.coursePackage.category && (
                            <AcademyCourseSidebarMetaRow
                                icon="Category"
                                name={localize("Kategori")}
                                value={state.coursePackage.category.name}
                            />
                        )}
                        <AcademyCourseSidebarMetaRow
                            icon="Clock"
                            name={localize("SenastUppdaterad")}
                            value={moment(state.coursePackage.lastChanged).format("LL")}
                        />
                        <AcademyCourseSidebarMetaRow
                            icon="Global"
                            name={localize("Sprak")}
                            value={Array.from(
                                new Set(
                                    state.coursePackage.courses.map(
                                        (c) => languages.find((x) => x.key == c.language.toString()).text
                                    )
                                )
                            ).join(", ")}
                        />
                        <AcademyCourseSidebarMetaRow
                            icon="book"
                            name={localize("COURSE_COUNT")}
                            value={state.coursePackage.courses.length.toString()}
                        />
                    </div>
                )}
            />

            <AcademyCourseSidebarSection
                onRender={() => (
                    <div className="AcademyCourseSidebarStatus">
                        <AcademyCourseProgress
                            userProgress={{
                                isCompleted: state.coursePackage.courses.every((c) => c.userProgress.isCompleted),
                                completionPercent: Math.round(
                                    state.coursePackage.courses.reduce(
                                        (acc, c) => acc + c.userProgress.completionPercent,
                                        0
                                    ) / state.coursePackage.courses.length
                                ),
                            }}
                        />

                        {isAdminOrEditor && !appMode && (
                            <div className="buttons">
                                <Link to={`/admin/learning/package/${state.coursePackage.id}/edit`}>
                                    <DefaultButton text={localize("EDIT_COURSE_PACKAGE")} />
                                </Link>
                            </div>
                        )}
                    </div>
                )}
            />
        </div>
    );

    if (state.isLoading) {
        return <SpintrLoader />;
    }

    return (
        <div className="AcademyCoursePackageView">
            <ContentWithInfoPanel template={1} visagePageStyle renderInfoPanel={renderSidebar}>
                <Helmet>
                    <title>{localize(props.academyAppName, true)}</title>
                </Helmet>
                <Breadcrumbs
                    hideInstance={props.appMode}
                    items={[
                        {
                            key: "0",
                            text: localize(props.academyAppName, true),
                            link: "/learning",
                        },
                        {
                            key: "1",
                            text: state.coursePackage.name,
                            link: "/learning/packages/" + state.coursePackage.id,
                        },
                    ]}
                />
                <div className="AcademyCoursePreview">
                    {state.coursePackage.imageUrl && <AcademyImage imageUrl={state.coursePackage.imageUrl} />}
                    <PageHeader title={localize(state.coursePackage.name)} />
                    <AcademyCourseMetaDataRow
                        date={state.coursePackage.lastChanged}
                        lessonCount={state.coursePackage.courses.length}
                        languages={Array.from(new Set(state.coursePackage.courses.map((c) => c.language)))}
                        isCoursePackage
                    />
                    {state.coursePackage.description && (
                        <Label className="course-description" color="dark-grey">
                            {state.coursePackage.description}
                        </Label>
                    )}
                </div>
                <div className="AcademyCourseList">
                    <div className="courses">
                        {state.coursePackage.courses.map((course, idx) => (
                            <AcademyCourseListItem
                                course={course}
                                coursePackageId={state.coursePackage.id}
                                hideActionMenu
                                locked={
                                    !state.coursePackage.optionalOrder &&
                                    !course.userProgress.isCompleted &&
                                    state.coursePackage.courses[idx - 1]?.userProgress?.isCompleted === false
                                }
                            />
                        ))}
                    </div>
                </div>
            </ContentWithInfoPanel>
            {props.isSmallViewMode && renderSidebar()}
        </div>
    );
};

const mapStateToProps = (state: Spintr.AppState, props) => ({
    ...props,
    currentUser: state.profile.active,
    appEnabled: state.app.items.some((app) => app.enabled && app.id === SpintrTypes.SpintrApp.Academy),
    appMode: state.ui.appMode,
    academyAppName: state.instance.get("academyAppName"),
    isSmallViewMode: state.ui.isSmallViewMode,
});

export default withRouter(connect(mapStateToProps)(AcadmeyCoursePackageView));
